import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

interface InspectionCarouselProps {
  inspectionState: any;
}

const InspectionCarousel: React.FC<InspectionCarouselProps> = ({
  inspectionState,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const slides = [
    {
      title: "✨ Propreté",
      gradient: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
      borderColor: "success.main",
      titleColor: "success.dark",
      items: [
        {
          label: "Intérieur",
          value: inspectionState.cleanliness.interior,
          type: "boolean",
        },
        {
          label: "Extérieur",
          value: inspectionState.cleanliness.exterior,
          type: "boolean",
        },
        {
          label: "Cabine",
          value: inspectionState.cleanliness.cabine,
          type: "boolean",
        },
      ],
    },
    {
      title: "🔧 Vérifications",
      gradient: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
      borderColor: "info.main",
      titleColor: "info.dark",
      items: [
        {
          label: "Phares",
          value: inspectionState.checklist.lights,
          type: "conforme",
        },
        {
          label: "Freins",
          value: inspectionState.checklist.brakes,
          type: "conforme",
        },
        {
          label: "Pneus",
          value: inspectionState.checklist.tires,
          type: "conforme",
        },
        {
          label: "Documents",
          value: inspectionState.checklist.documents,
          type: "conforme",
        },
      ],
    },
    {
      title: "💥 Dégradations",
      gradient: "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
      borderColor: "error.main",
      titleColor: "error.dark",
      items: [
        {
          label: "Rayures",
          value: inspectionState.degradations.details.rayures,
          type: "number",
          color: "warning",
        },
        {
          label: "Chocs",
          value: inspectionState.degradations.details.chocs,
          type: "number",
          color: "error",
        },
        {
          label: "Manquants",
          value: inspectionState.degradations.details.manquant,
          type: "number",
          color: "warning",
        },
        {
          label: "Cassés",
          value: inspectionState.degradations.details.casse,
          type: "number",
          color: "error",
        },
      ],
      total:
        inspectionState.degradations.details.rayures +
        inspectionState.degradations.details.chocs +
        inspectionState.degradations.details.manquant +
        inspectionState.degradations.details.casse,
    },
  ];

  const handlePrev = () => {
    setActiveSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const currentSlide = slides[activeSlide];

  return (
    <Box sx={{ position: "relative", mb: 3 }}>
      {/* Carrousel */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Bouton Précédent */}
        <IconButton
          onClick={handlePrev}
          sx={{
            bgcolor: "white",
            boxShadow: 1,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <ChevronLeft />
        </IconButton>

        {/* Carte active */}
        <Box sx={{ flex: 1, maxWidth: { xs: "100%", sm: 600 }, mx: "auto" }}>
          <Card
            sx={{
              height: "100%",
              background: currentSlide.gradient,
              borderLeft: 4,
              borderColor: currentSlide.borderColor,
              boxShadow: 2,
              transition: "all 0.3s ease-in-out",
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: currentSlide.titleColor,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                }}
              >
                {currentSlide.title}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {currentSlide.items.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.9rem", sm: "0.95rem" }, fontWeight: 500 }}
                    >
                      {item.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        item.type === "boolean"
                          ? item.value
                            ? "✓"
                            : "✗"
                          : item.type === "conforme"
                          ? item.value === "conforme"
                            ? "✓"
                            : "✗"
                          : item.value
                      }
                      color={
                        item.type === "boolean"
                          ? item.value
                            ? "success"
                            : "error"
                          : item.type === "conforme"
                          ? item.value === "conforme"
                            ? "success"
                            : "error"
                          : item.value > 0
                          ? item.color || "warning"
                          : "default"
                      }
                      sx={{
                        minWidth: 36,
                        height: 22,
                        fontSize: "0.8rem",
                        fontWeight: item.type === "number" ? 600 : 500,
                      }}
                    />
                  </Box>
                ))}
              </Box>

              {/* Total pour dégradations */}
              {currentSlide.total !== undefined && currentSlide.total > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    pt: 1.5,
                    borderTop: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="error"
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    }}
                  >
                    Total: {currentSlide.total} dégât(s)
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Bouton Suivant */}
        <IconButton
          onClick={handleNext}
          sx={{
            bgcolor: "white",
            boxShadow: 1,
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Indicateurs de slides */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mt: 2,
        }}
      >
        {slides.map((slide, idx) => (
          <Chip
            key={idx}
            size="small"
            label={slide.title}
            onClick={() => setActiveSlide(idx)}
            sx={{
              cursor: "pointer",
              bgcolor: idx === activeSlide ? slide.borderColor : "grey.200",
              color: idx === activeSlide ? "white" : "grey.700",
              fontWeight: idx === activeSlide ? 600 : 400,
              transition: "all 0.3s",
              "&:hover": {
                bgcolor: idx === activeSlide ? slide.borderColor : "grey.300",
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default InspectionCarousel;

